import { FC, SetStateAction, useEffect, useState } from 'react'

import { Field, Form, FormikProps } from 'formik'

import Box from 'app/components/design-system-components/Box'
import { Button } from 'app/components/design-system-components/Button'
import Flex from 'app/components/design-system-components/Flex'
import {
  AutocompleteField,
  TextArea,
} from 'app/components/design-system-components/inputs'
import { TagsInputField } from 'app/components/design-system-components/inputs/TagsInput/TagsInput'
import { StyledRecipientField } from 'app/components/design-system-components/InviteToApply/styles'
import { formFields } from 'app/components/design-system-components/SendNotificationToOwner/form.utils'
import { MessagePreview } from 'app/components/design-system-components/SendNotificationToOwner/Preview'
import { Spinner } from 'app/components/design-system-components/Spinner'
import { FieldWrapper } from 'app/forms/FieldWrapper'
import { debounce, STANDARD_TIMEOUT } from 'app/helpers/debounce'
import { searchTeamContacts } from 'app/services/http/teams/contacts'

export const getOptionAndMetadataForApplicant = (
  email = '',
  firstName = '',
  lastName = '',
  phone = '',
) => {
  const formedOption = {
    renterEmail: email,
    renterFirstName: firstName,
    renterLastName: lastName,
    renterContactNumber: phone,
  }
  const formedOptionMeta = {
    id: email,
    displayText: email,
  }

  return {
    option: formedOption,
    optionMeta: formedOptionMeta,
  }
}

type SearchedContacts = {
  contacts: [
    {
      guidID: string
      email: string
      firstName: string
      lastName: string
      phone: string
    },
  ]
}

interface IFormBodyProps {
  formBag: FormikProps<any>
  teamGUID: string
  toggleModal: () => void
  isPayloadSubmitting?: boolean
}

export const FormBody: FC<IFormBodyProps> = ({
  teamGUID = '',
  formBag: { values, setFieldValue, validateField },
  toggleModal = () => {},
  isPayloadSubmitting = false,
}) => {
  const [showPreview, setShowPreview] = useState(false)

  const [contactOptions, setContactOptions] = useState([])
  const [loadingcContacts, setLoadingContacts] = useState(false)
  const setSearchedForContact = useState('')[1]

  useEffect(() => {
    const { option: contact } = values[formFields.contact] || {}
    if (contact) {
      const {
        email = '',
        firstName = '',
        lastName = '',
        phone = '',
      } = contact || {}
      if (email && firstName && lastName && phone) {
        const combinedOption = getOptionAndMetadataForApplicant(
          email,
          firstName,
          lastName,
          phone,
        )

        setFieldValue(formFields.owners, [
          ...values[formFields.owners],
          combinedOption,
        ])
        setFieldValue(formFields.contact, null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values[formFields.contact]])

  const onContactInputChange = (searchText: SetStateAction<string>) => {
    setSearchedForContact(searchText)
    onSearchContacts(searchText)
  }

  const onSearchContacts = debounce((searchText: string) => {
    setLoadingContacts(true)
    return searchTeamContacts(teamGUID, searchText)
      .then((response: SearchedContacts) => {
        const filteredContacts = response?.contacts || []
        return filteredContacts.map((contact) => ({
          optionMeta: {
            id: contact.guidID,
            displayText:
              contact?.firstName +
              ' ' +
              contact?.lastName +
              ' ' +
              contact?.phone +
              ' ' +
              contact?.email,
          },
          option: contact,
        }))
      })
      .then((filteredPropertyOptions: []) => {
        setContactOptions(filteredPropertyOptions)
      })
      .finally(() => {
        setLoadingContacts(false)
      })
  }, STANDARD_TIMEOUT)

  const getCombinedOwners = (enteredValue: string = '') => {
    if (!enteredValue.trim()) {
      return values[formFields.owners] || []
    }

    const allOwners = values[formFields.owners] || []
    const allOwnersWithSearchedValue = enteredValue
      ? [{ option: enteredValue }, ...allOwners]
      : allOwners

    const manuallyAddedOwners = allOwnersWithSearchedValue.filter(
      (owner: { option: any }) =>
        typeof owner.option === 'string' && owner.option.trim(),
    )

    const ownersAddedThroughContact = allOwnersWithSearchedValue.filter(
      (owner: { option: any }) => typeof owner?.option !== 'string',
    )
    const mappedManuallyAddedApps = manuallyAddedOwners.map(
      ({ option }: any) => {
        return getOptionAndMetadataForApplicant(option)
      },
    )

    return [...ownersAddedThroughContact, ...mappedManuallyAddedApps]
  }

  const onEnterPressed = (event: {
    which: number
    preventDefault: () => any
    target: { value: string }
  }) => {
    const enteredValue = event.target.value.trim()
    if (enteredValue === '') {
      event.preventDefault()
      return
    }

    const owners = getCombinedOwners(enteredValue)
    setFieldValue(formFields.owners, [...owners])
    setSearchedForContact('')
    setFieldValue(formFields.contact, null)
    validateField(formFields.owners)
    event.which === 13 && event.preventDefault()
  }

  return (
    <Form>
      {showPreview ? (
        <MessagePreview {...values} />
      ) : (
        <>
          <FieldWrapper
            id={formFields.contact}
            label="TO"
            required={true}
            name={formFields.contact}
            labelProps={undefined}
            my={5}
          >
            <AutocompleteField
              name={formFields.contact}
              placeholder="Search Name, mobile, enter to add"
              options={contactOptions}
              onChangeText={onContactInputChange}
              isLoading={loadingcContacts}
              onEnterPressed={onEnterPressed}
            />
          </FieldWrapper>
          <StyledRecipientField id={formFields.owners} name={formFields.owners}>
            <TagsInputField
              id={formFields.owners}
              name={formFields.owners}
              disabled={true}
            />
          </StyledRecipientField>
          <FieldWrapper
            id={formFields.message}
            label="COMMENT"
            required={false}
            name={formFields.message}
            mb={5}
          >
            <Field name={formFields.message} as={TextArea} rows={10} />
          </FieldWrapper>
        </>
      )}
      <Flex width="100%" mt={5}>
        <Flex width="50%">
          <Button
            variant="solidSecondary"
            mr={3}
            width="50%"
            type="button"
            onClick={(event: { preventDefault: () => void }) => {
              event?.preventDefault()
              toggleModal()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="solidSecondary"
            mr={3}
            width="50%"
            type="button"
            onClick={(event: { preventDefault: () => void }) => {
              event?.preventDefault()
              setShowPreview(!showPreview)
            }}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
        </Flex>
        <Box width="50%">
          <Button
            variant="solidSuccess"
            width="100%"
            type="submit"
            disabled={isPayloadSubmitting}
            mr={3}
          >
            <Flex alignItems="center" justifyContent="center">
              <Box mr={3}>Send</Box>
              {isPayloadSubmitting && <Spinner />}
            </Flex>
          </Button>
        </Box>
      </Flex>
    </Form>
  )
}
